<script setup lang="ts">
import { computed, ref } from 'vue';

import type { PremadeTrip } from '@apps/main/new-desktop/core/models';
import { PremadeTripType } from '@apps/main/new-desktop/core/models';

import { AppTag } from '@libs/ui';

import ReadMoreButton from './ReadMoreButton.vue';

interface PremadeTripHeaderDesktopProps {
  tripDetails: PremadeTrip;
}

const props = defineProps<PremadeTripHeaderDesktopProps>();

interface PremadeTripHeaderDesktopEmits {
  (eventName: 'showFullDescription'): void;
}

const emit = defineEmits<PremadeTripHeaderDesktopEmits>();

const COLUMNS_HEIGHT_PX = 300;
const descriptionContainer = ref<HTMLDivElement | undefined>(undefined);

const shouldShowReadMoreButton = computed(() => {
  if (descriptionContainer.value) {
    return descriptionContainer.value.offsetHeight > COLUMNS_HEIGHT_PX;
  }
  return false;
});
</script>

<template>
  <div class="premade-trip-header-desktop">
    <div class="columns" :style="{ height: `${COLUMNS_HEIGHT_PX}px` }">
      <div ref="descriptionContainer" class="left-column">
        <AppTag
          v-if="props.tripDetails.type === PremadeTripType.AI"
          class="ai-generated-tag"
          type="info"
          :icon="{ name: 'ai', size: '16' }"
        >
          <template #title>{{ $t('planner.ai_generated') }}</template>
        </AppTag>
        <div class="heading">{{ props.tripDetails.headline }}</div>
        <slot name="description" />

        <div v-if="shouldShowReadMoreButton" class="read-more-button-wrapper">
          <ReadMoreButton @click="emit('showFullDescription')" />
        </div>
      </div>
      <div class="right-column">
        <img
          :src="props.tripDetails.imageUrl"
          class="image"
          :alt="$t('a11y.trip_image')"
          :style="{ 'max-height': `${COLUMNS_HEIGHT_PX}px` }"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.premade-trip-header-desktop {
  padding: var(--spacing-24);

  .columns {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: min-content;
    gap: var(--spacing-24);

    overflow: hidden;
    position: relative;

    .left-column {
      .ai-generated-tag {
        margin-bottom: var(--spacing-16);
        @include font-label-s-light;
        padding: var(--spacing-4) var(--spacing-8);
      }

      .heading {
        padding: var(--spacing-8) 0;
        @include font-heading-5;
        color: var(--color-neutral-70);
      }

      .read-more-button-wrapper {
        position: absolute;
        left: 0;
        bottom: 0;
        width: 50%;
      }
    }

    .right-column {
      .image {
        border-radius: var(--border-radius-12);
        width: 100%;
        height: auto;
        object-fit: cover;
        aspect-ratio: 16/10;
      }
    }
  }
}
</style>
